import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TheBorder} from "../TheBorder";
import {TheBorderGameAssets} from "../TheBorderGameAssets";
import {BasicBitmapCountUp} from "../components/BasicBitmapCountUp";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineLite, TweenLite} from "gsap";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TheBorderSound} from "../sound/TheBorderSound";
import {TheBorderGameMode} from "../server/TheBorderGameMode";

export class TheBorderMaxWinView extends PIXI.Container implements StageLayer {
    private _animation: PIXI.spine.Spine;
    private _counter: BasicBitmapCountUp;
    private _seized: PIXI.Sprite;
    private _and: PIXI.Sprite;
    private _destroy: PIXI.Sprite;
    private _winTimesX: PIXI.Sprite;
    private _winTimesXAdd: PIXI.Sprite;
    private _blast: TimelineSprite;
    private _button: ContinueButton;
    private _clickCallback: () => void;
    private _winTimesTl: TimelineLite;

    constructor() {
        super();
        this.name = "TheBorderMaxWin";
        this.init();
        this.onResize();
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._seized.position.set(-70, 100);
            this._and.position.set(260, 100);
            this._destroy.position.set(690, 100);

            this._button.position.set(360, 615);
        } else {
            this._seized.position.set(360, -120);
            this._and.position.set(360, 0);
            this._destroy.position.set(360, 120);

            const bounds: ScreenBounds = NolimitApplication.screenBounds,
                centerBottom: number = (bounds.bottom - 610) * 0.5 + 610;

            this._button.position.set(360, centerBottom - 80);
        }
    }

    public playMaxWin(cb: () => void): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            fps: number = 30,

            countUpDuration: number = 5.333;//#208  ,180 / fps; //178 /fps

        tl.add([
            () => {
                TheBorder.keypadPlugin.setZeroBetSpinCounter(-1);
                TheBorder.scatterSymbols.turnOffAllScatterSymbols();
                TheBorder.xNudgeSymbols.turnOffAllXNudgeSymbols();
                TheBorder.xSplitSymbols.turnOffAllXSplitSymbols();
                TheBorder.xWaysSymbols.turnOffAllXWaysSymbols();
                TheBorder.xBombSymbols.turnOffAllXBombSymbols();

                this.alpha = 1;

                this._clickCallback = cb;

                [this._seized, this._and, this._destroy, this._counter].forEach((item: PIXI.DisplayObject) => {
                    item.visible = true;
                });

                this.resetSpine();
                this._animation.state.setAnimation(0, "In", false);

                TweenLite.fromTo([this._seized.scale, this._and.scale, this._destroy.scale], {
                        x: 0.8,
                        y: 0.8
                    },
                    {
                        duration: countUpDuration,
                        x: 0.9,
                        y: 0.9
                    });

                this._counter.scale.set(1);
                TweenLite.to(this._counter.scale, {
                    duration: 176 / fps,
                    x: 0.8,
                    y: 0.8
                });

                this._winTimesXAdd.alpha = 0;
                this._winTimesXAdd.visible = true;
                TheBorder.soundPlayer.fadeCurrentMusicAndResetCurrentMusic({
                    name: "",
                    to: 0,
                    duration: 1
                });
                TheBorder.soundPlayer.playSound(TheBorderSound.CAP_WIN_COUNT_UP);
                TheBorder.soundPlayer.playSound(TheBorderSound.CAP_WIN_LOOP);
            },
            this._counter.getCountAnimation(0, TheBorder.model.gameData.accumulatedRoundWin, countUpDuration)
        ]);
        tl.to(this._counter.scale, {
            duration: 4 / fps,
            x: 1.2,
            y: 1.2
        }, (176 / fps));
        tl.add([
            this._blast.getAnimationAutoShowHide(true, true, [1, 15]),
            () => {
                this.resetSpine();
                this._animation.state.setAnimation(0, "Loop", true);
            }
        ], countUpDuration);
        tl.add(() => TheBorder.backgroundElements.setMode(TheBorderGameMode.NORMAL));
        tl.add(() => TheBorder.winMultiplierView.activateMainGameMultiplier());
        tl.to(this._counter.scale, {
            duration: 40 / fps,
            x: 1,
            y: 1
        }, countUpDuration);
        tl.add(() => {
            this._button.alpha = 0;
            this._button.visible = true;

            this._winTimesX.visible = true;
            this._winTimesX.alpha = 0;
            TweenLite.to(this._winTimesX, {
                duration: 0.5,
                alpha: 1
            });

            this.resetSpine();
            this._animation.state.setAnimation(0, "Loop", true);

            TweenLite.to(this._counter, {
                duration: 0.5,
                alpha: 0
            });
        });
        tl.to(this._button, {duration: 0.2, alpha: 1});
        tl.add(() => {
            this._button.enable(true);

            this.animateWinTimesX();
        });

        return tl;
    }

    private init(): void {
        this._animation = new PIXI.spine.Spine(TheBorder.assetLoader.getSpineAsset(TheBorderGameAssets.MAX_WIN));
        this._animation.visible = false;
        this._animation.position.set(360);

        this._seized = new PIXI.Sprite(TheBorder.assetLoader.getTextures("wincap/seized")[0]);
        this._seized.visible = false;
        this._seized.anchor.set(0.5);

        this._and = new PIXI.Sprite(TheBorder.assetLoader.getTextures("wincap/and")[0]);
        this._and.visible = false;
        this._and.anchor.set(0.5);

        this._destroy = new PIXI.Sprite(TheBorder.assetLoader.getTextures("wincap/destroyed")[0]);
        this._destroy.visible = false;
        this._destroy.anchor.set(0.5);

        this._counter = new BasicBitmapCountUp({
            fontName: TheBorderGameAssets.BITMAP_FONT_WIN_NUMBERS,
            fontSize: 190,
            tint: 0xffffff
        }, 720, new PIXI.Point(0.5, 0.5));
        this._counter.visible = false;
        this._counter.position.set(360);

        this._winTimesX = new PIXI.Sprite(TheBorder.assetLoader.getTextures("wincap/winTimesX")[0]);
        this._winTimesX.visible = false;
        this._winTimesX.anchor.set(0.5);
        this._winTimesX.position.set(360);

        this._winTimesXAdd = new PIXI.Sprite(TheBorder.assetLoader.getTextures("wincap/winTimesX")[0]);
        this._winTimesXAdd.visible = false;
        this._winTimesXAdd.blendMode = PIXI.BLEND_MODES.ADD;
        this._winTimesXAdd.anchor.set(0.5);
        this._winTimesXAdd.position.set(360);

        this._blast = new TimelineSprite(TheBorder.assetLoader.getTextures("Blast_Xbomb/Blast_Xbomb_"));
        this._blast.hide();
        this._blast.blendMode = PIXI.BLEND_MODES.ADD;
        this._blast.anchor.set(0.5);
        this._blast.scale.set(5);
        this._blast.position.set(360);

        this._button = new ContinueButton();
        this._button.addClickCallback(() => this.onButtonClick());
        this._button.position.set(360, 615);
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false);
        this._button.visible = false;

        this.addChild(this._animation, this._seized, this._and, this._destroy, this._counter, this._winTimesX, this._winTimesXAdd, this._blast, this._button);
    }

    private onButtonClick(): void {
        this._button.enable(false);
        TheBorder.soundPlayer.fadeMusic({
            name: TheBorderSound.CAP_WIN_LOOP,
            to: 0,
            duration: 100
        });
        TheBorder.soundPlayer.playSound(TheBorderSound.CAP_WIN_CONTINUE);
        TheBorder.soundPlayer.switchMusic(TheBorderGameMode.NORMAL);

        TweenLite.to(this, {
            duration: 0.3,
            alpha: 0,
            onComplete: () => {
                TheBorder.soundPlayer.stopSound(TheBorderSound.CAP_WIN_LOOP);
                [this._animation, this._seized, this._and, this._destroy, this._winTimesX, this._winTimesXAdd, this._button].forEach((item: PIXI.DisplayObject) => {
                    item.visible = false;
                });

                this._blast.hide();
                this._counter.visible = false;
                this._counter.alpha = 1;
                this._animation.state.clearTracks();

                this._clickCallback();
            }
        });

        if (this._winTimesTl) {
            this._winTimesTl.pause();
            this._winTimesTl.kill();
        }
    }

    private resetSpine(): void {
        if (!this._animation.visible) {
            this._animation.visible = true;
            this._animation.updateTransform();
        }
        this._animation.state.clearTracks();
        this._animation.skeleton.setSlotsToSetupPose();
        this._animation.skeleton.setToSetupPose();
        this._animation.state.setEmptyAnimation(0, 0);
    }

    private animateWinTimesX(): void {
        const fps: number = 30;

        this._winTimesTl = new TimelineLite({
            onComplete: () => {
                this.animateWinTimesX();
            }
        });

        this._winTimesTl.to([this._winTimesX.scale, this._winTimesXAdd.scale], {
            duration: 15 / fps,
            x: 1.05,
            y: 1.05
        }, 0);
        this._winTimesTl.to(this._winTimesXAdd, {
            duration: 15 / fps,
            alpha: 0.5
        }, 0);
        this._winTimesTl.to([this._winTimesX.scale, this._winTimesXAdd.scale], {
            duration: 15 / fps,
            x: 1,
            y: 1
        }, (15 / fps));
        this._winTimesTl.to(this._winTimesXAdd, {
            duration: 15 / fps,
            alpha: 0
        }, (15 / fps));
        this._winTimesTl.to(this, {
            duration: 5 / fps
        }, (30 / fps));
    }
}